import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IDENTITY_CARD_WIDTH_PROPERTY } from '../shared/identityCardWidth';
import { readRowMeasurements } from './readRowMeasurements';
import {
  hasArrived,
  isInFlight,
  isSmoothInFlight,
  isSameMore,
  isSameRow,
  NO_MORE,
  NOTHING_TO_SCROLL,
  planRow,
  readMore,
  restingLeft,
  startPending,
  type MeasuredRow,
  type More,
  type PendingScroll,
} from './measuredRow';
import {
  firstIndexAt,
  isCardFullyInView,
  startAfterPage,
  startToReveal,
  startToShowChosen,
} from './wholeCardRow';

// `instant`, never `auto`: the page's global style sets `scroll-behavior: smooth`, which `auto`
// would follow.
const INSTANT: ScrollBehavior = 'instant';

// A smooth scroll, unless the customer has asked their device for less motion.
function motionBehavior(): ScrollBehavior {
  const reducesMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  return reducesMotion ? INSTANT : 'smooth';
}

// Hands the width on to the cards: every card in the row reads it (the cards fall back to the
// theme's width when it is not set).
function setCardWidth(row: HTMLElement, cardWidth: number | null) {
  if (cardWidth === null) {
    row.style.removeProperty(IDENTITY_CARD_WIDTH_PROPERTY);
    return;
  }
  row.style.setProperty(IDENTITY_CARD_WIDTH_PROPERTY, `${cardWidth}px`);
}

type WholeCardRowInput = {
  /**
   * The position, among the identity cards, of the card that is chosen, or null when the chosen
   * option is not one of them. The row shows it whenever it, or the row's size, changes.
   */
  chosenIndex: number | null;
};

type WholeCardRow = {
  rowRef: RefObject<HTMLDivElement>;
  scrollerRef: RefObject<HTMLDivElement>;
  reservedRef: RefObject<HTMLDivElement>;
  probeRef: RefObject<HTMLDivElement>;
  clearanceRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  /** Scrolls one page of whole cards (the number in view) to the left or right. */
  scrollByPage: (direction: 'left' | 'right') => void;
  /**
   * Makes sure the identity card at `index` is fully in view, scrolling the row by the least
   * whole-card step that shows it. Used when a card takes focus, however it got it.
   */
  revealCard: (index: number) => void;
  overflowing: boolean;
  more: More;
};

/**
 * Lays a row of identity cards out so that only whole cards show, and reports what lies beyond
 * the edges. The room, the card width and the minimum gap are measured from the browser's layout
 * (never counted from the identities), and measured again whenever the row is resized and after
 * every render, before the first paint.
 *
 * The room is divided into as many slots as fit a minimum card with the minimum gap between them,
 * and every card is widened to fill its slot: the gap stays the minimum and the leftover width
 * goes into the cards. The width reaches the cards through a custom property on the row, so that
 * the visible cards fill the row from edge to edge. Returns the refs for the row, the scrolling
 * box and the pinned card's cell, the scroll handler for the box, and what was measured.
 */
export function useWholeCardRow({ chosenIndex }: Readonly<WholeCardRowInput>): WholeCardRow {
  const rowRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const reservedRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const clearanceRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<MeasuredRow>(NOTHING_TO_SCROLL);
  const pendingRef = useRef<PendingScroll | null>(null);
  const chosenIndexRef = useRef(chosenIndex);
  chosenIndexRef.current = chosenIndex;
  // The customer has scrolled the chosen card out of view by hand, and the row leaves it there.
  const userAwayRef = useRef(false);
  const [row, setRow] = useState<MeasuredRow>(NOTHING_TO_SCROLL);
  const [more, setMore] = useState<More>(NO_MORE);

  const updateMore = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const next = readMore(measuredRef.current, scroller);
    setMore((previous) => (isSameMore(previous, next) ? previous : next));
  }, []);

  // Scrolls the row so that the card at `start` is at its left edge. The row remembers that it
  // asked, so the scroll that follows is not taken for the customer's own.
  const scrollToStart = useCallback(
    (start: number, behavior: ScrollBehavior) => {
      const scroller = scrollerRef.current;
      if (!scroller) {
        return;
      }
      const left = start * measuredRef.current.view.step;
      pendingRef.current = startPending(left, behavior === 'smooth');
      scroller.scrollTo({ left, behavior });
      updateMore();
    },
    [updateMore],
  );

  // Shows the chosen card by the least move that shows it whole.
  const showChosenCard = useCallback(
    (behavior: ScrollBehavior) => {
      const scroller = scrollerRef.current;
      const measured = measuredRef.current;
      const index = chosenIndexRef.current;
      if (!scroller || index === null || !measured.overflowing) {
        return;
      }
      const scrollLeft = restingLeft(scroller, pendingRef.current);
      const start = startToShowChosen({ ...measured.view, index, scrollLeft });
      if (start !== null) {
        scrollToStart(start, behavior);
      }
    },
    [scrollToStart],
  );

  // After the row changes size the cards sit at new places: rest on the nearest card again.
  const alignToNearestCard = useCallback(() => {
    const scroller = scrollerRef.current;
    const { step } = measuredRef.current.view;
    if (scroller && step > 0) {
      scrollToStart(firstIndexAt(scroller.scrollLeft, step), INSTANT);
    }
  }, [scrollToStart]);

  // What the row does about the chosen card, as the board's row does. It runs from the measuring,
  // which runs when the row is rendered or resized, and never from a scroll:
  //  - the first time, the chosen card is shown at once;
  //  - when the choice changes, the customer is no longer away from it, and it is shown;
  //  - when the row changes size, it is shown again, unless the customer scrolled it away by hand,
  //    and then the row only rests on a card.
  const seenRef = useRef<{ index: number | null; measured: MeasuredRow } | null>(null);
  const followChosen = useCallback(() => {
    const [seen, index, measured] = [seenRef.current, chosenIndexRef.current, measuredRef.current];
    seenRef.current = { index, measured };
    if (seen === null) {
      showChosenCard(INSTANT);
    } else if (seen.index !== index) {
      userAwayRef.current = false;
      showChosenCard(motionBehavior());
    } else if (!isSameRow(seen.measured, measured)) {
      if (userAwayRef.current) {
        alignToNearestCard();
      } else {
        showChosenCard(INSTANT);
      }
    }
  }, [alignToNearestCard, showChosenCard]);

  const measure = useCallback(() => {
    const [rowElement, scroller, probe] = [rowRef.current, scrollerRef.current, probeRef.current];
    if (!rowElement || !scroller || !probe) {
      return;
    }
    const measured = readRowMeasurements({
      row: rowElement,
      scroller,
      probe,
      clearance: clearanceRef.current,
      pinned: reservedRef.current !== null,
    });
    const { row: next, cardWidth } = planRow(measured);
    setCardWidth(rowElement, cardWidth);
    measuredRef.current = next;
    followChosen();
    setRow((previous) => (isSameRow(previous, next) ? previous : next));
    updateMore();
  }, [followChosen, updateMore]);

  // No dependency list on purpose: it re-reads after each render (the cards may have changed
  // without the row changing size), and `setState` bails out when nothing changed.
  useLayoutEffect(measure);

  useLayoutEffect(() => {
    const rowElement = rowRef.current;
    if (!rowElement || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(rowElement);
    return () => observer.disconnect();
  }, [measure]);

  // A scroll the row did not ask for is the customer's own. If it leaves the chosen card out of
  // view, the row leaves it there. The row's own scroll is over when its scroll event shows that
  // it has arrived, not when the position has: the browser moves an instant scroll at once and
  // sends the event a frame later, and that event is still the row's own.
  const onScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    const index = chosenIndexRef.current;
    if (scroller && !isInFlight(pendingRef.current)) {
      const { view } = measuredRef.current;
      const scrollLeft = scroller.scrollLeft;
      userAwayRef.current = index !== null && !isCardFullyInView({ ...view, index, scrollLeft });
    }
    if (scroller && hasArrived(scroller, pendingRef.current)) {
      pendingRef.current = null;
    }
    updateMore();
  }, [updateMore]);

  const scrollByPage = useCallback(
    (direction: 'left' | 'right') => {
      const scroller = scrollerRef.current;
      const { view } = measuredRef.current;
      if (!scroller) {
        return;
      }
      scrollToStart(
        startAfterPage({
          firstIndex: firstIndexAt(restingLeft(scroller, pendingRef.current), view.step),
          direction,
          count: view.count,
          visibleCount: view.visibleCount,
        }),
        motionBehavior(),
      );
    },
    [scrollToStart],
  );

  // Shows the card at `index` whole by the least move. `fromActual` judges it from where the row
  // really is, not where the row's own scroll in flight is going.
  const showCard = useCallback(
    (index: number, fromActual: boolean) => {
      const scroller = scrollerRef.current;
      const { overflowing, view } = measuredRef.current;
      if (!scroller || !overflowing || index < 0 || index >= view.count) {
        return;
      }
      const scrollLeft = fromActual
        ? scroller.scrollLeft
        : restingLeft(scroller, pendingRef.current);
      if (isCardFullyInView({ ...view, index, scrollLeft })) {
        return;
      }
      scrollToStart(
        startToReveal({
          index,
          firstIndex: firstIndexAt(scrollLeft, view.step),
          count: view.count,
          visibleCount: view.visibleCount,
        }),
        INSTANT,
      );
    },
    [scrollToStart],
  );

  // Now, for a card the row focused without the browser's own scroll (the arrow keys). And once
  // more a frame later, for a card that took focus by other means: the browser may scroll to it
  // after the focus event, and leave it cut off or off the card positions.
  const frameRef = useRef<number | null>(null);
  const revealCard = useCallback(
    (index: number) => {
      showCard(index, false);
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        if (!isSmoothInFlight(pendingRef.current)) {
          showCard(index, true);
        }
      });
    },
    [showCard],
  );
  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    },
    [],
  );

  return {
    rowRef,
    scrollerRef,
    reservedRef,
    probeRef,
    clearanceRef,
    onScroll,
    scrollByPage,
    revealCard,
    overflowing: row.overflowing,
    more,
  };
}
